import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import {
    faBan,
    faClock,
    faPiggyBank,
    faLock,
    faLockOpen,
    faRunning,
    faCheckCircle,
    faTimesCircle,
    faUserCheck,
    faUserTimes,
    faArchive,
    faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useState } from "react";
import styled from "styled-components";
import AuthContext from "../AuthContext";
import { EventPresentRequest } from "consumerhouse-shared";
import { RegistrationStatus } from "consumerhouse-shared";
import { TestStatus } from "consumerhouse-shared";
import { Translation, useLanguage } from "../useLanguage";
import { capitalize } from "../util";

const BadgeContainer = styled.span<{ color: string }>`
    font-size: 0.7em;
    padding: 0.15em 0.5em;
    background: ${(props) => props.color};
    color: white;
    border-radius: 0.5em;
    border: 2px solid transparent;
    transition: 100ms;
    white-space: nowrap;

    &.clickable {
        cursor: pointer;
    }

    &.clickable:hover {
        transition: 100ms;
        color: ${(props) => props.color};
        background: transparent;
        border: 2px solid ${(props) => props.color};
    }
`;

export type BadgeProps = {
    color?: string;
    icon?: IconDefinition;
    children?: React.ReactNode;
    onClick?: (ev: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
};

export function Badge(props: BadgeProps) {
    return (
        <BadgeContainer className={props.onClick && "clickable"} onClick={props.onClick} color={props.color ?? "red"}>
            {props.icon && <FontAwesomeIcon icon={props.icon} style={{ margin: "0 0.3em 0 0" }} />}
            {props.children}
        </BadgeContainer>
    );
}

export function getIconForRegistrationStatus(status: RegistrationStatus) {
    switch (status) {
        case "waiting":
            return faClock;
        case "running":
            return faRunning;
        case "done":
            return faPiggyBank;
        case "cancelled":
        case "userCancelled":
            return faBan;
        case "noshow":
            return faUserSlash;
        default:
            throw new Error("Invalid status " + status);
    }
}

export function getColorForRegistrationStatus(status: RegistrationStatus) {
    switch (status) {
        case "waiting":
            return "#65c";
        case "running":
            return "#392";
        case "done":
            return "#aa2";
        case "cancelled":
        case "userCancelled":
        case "noshow":
            return "#b64";
        // return "#d47426";
        default:
            throw new Error("Invalid status " + status);
    }
}

export function getIconForTestStatus(status: TestStatus) {
    switch (status) {
        case "open":
            return faLockOpen;
        case "closed":
            return faLock;
        case "hidden":
            return faEyeSlash;
        case "archived":
            return faArchive;
        default:
            throw new Error("Invalid status " + status);
    }
}

export function getColorForTestStatus(status: TestStatus) {
    switch (status) {
        case "open":
            return "#4aa842";
        case "closed":
            return "#ec454f";
        case "hidden":
            return "#8f7b4d";
        case "archived":
            return "#9d4fca";
        default:
            throw new Error("Invalid status " + status);
    }
}

export function getNameForTestStatus(status: TestStatus) {
    switch (status) {
        case "open":
            return "Open";
        case "closed":
            return "Gesloten";
        case "hidden":
            return "Verborgen";
        case "archived":
            return "Gearchiveerd";
        default:
            throw new Error("Invalid status " + status);
    }
}

export function RegistrationStatusBadge(props: { status: RegistrationStatus; admin?: boolean }) {
    const language = useLanguage();
    return (
        <Badge
            children={props.admin ? capitalize(props.status) : language(("registration-badge-" + props.status) as Translation)}
            color={getColorForRegistrationStatus(props.status)}
            icon={getIconForRegistrationStatus(props.status)}
        />
    );
}

export type OpenCloseBadgeProps = {
    open: boolean;
};

export function OpenCloseBadge(props: OpenCloseBadgeProps) {
    return <Badge icon={props.open ? faLockOpen : faLock} color={props.open ? "#56a44f" : "#ec454f"} children={props.open ? "Open" : "Gesloten"} />;
}

export function TestStatusBadge(props: { status: TestStatus }) {
    return (
        <Badge icon={getIconForTestStatus(props.status)} color={getColorForTestStatus(props.status)} children={getNameForTestStatus(props.status)} />
    );
}

export function PresentBadge({ present, eventId, userId }: { present: boolean; userId?: number; eventId?: string }) {
    const [isPresent, setIsPresent] = useState(present);
    const { client } = useContext(AuthContext);

    return (
        <Badge
            onClick={async () => {
                if (!userId || !eventId) return;
                await client.fetch("/event/present", {
                    eventId,
                    userId,
                    present: !isPresent,
                });
                setIsPresent(!isPresent);
            }}
            icon={isPresent ? faUserCheck : faUserTimes}
            color={isPresent ? "#56a44f" : "#ec454f"}
            children={isPresent ? "Aanwezig" : "Afwezig"}
        />
    );
}

export function SatisfiesBadge({ satisfies }: { satisfies: boolean }) {
    return (
        <FontAwesomeIcon
            style={{
                color: satisfies ? "#56a44f" : "#ec454f",
            }}
            icon={satisfies ? faCheckCircle : faTimesCircle}
        />
    );
}
